import { useMemo, useState } from 'react';
import { Copy, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { blockSchema, type Block, type BlockType, type Role } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { SortableList } from '@/components/ui/SortableList.js';
import { ConfirmDialog } from '@/components/ui/Modal.js';
import { cn } from '@/lib/cn.js';
import { BLOCK_META, createBlock, newBlockId } from './block-catalog.js';
import { AddBlockModal } from './AddBlockModal.js';
import { BlockAppearance } from './BlockAppearance.js';
import { BlockDataForm } from './forms/index.js';

export type BlockConstructorProps = {
  blocks: readonly Block[];
  onChange: (blocks: Block[]) => void;
  role: Role;
  /** Sahifada nechta blok bo'lishi mumkin (`EMBEDDED_LIMITS.PAGE_BLOCKS`). */
  max?: number;
};

/**
 * Blok konstruktori — admin panelning eng murakkab ekrani (D4).
 *
 * Ikki panel: chapda bloklar ro'yxati (surish, ko'rinuvchanlik, dublikat,
 * o'chirish), o'ngda tanlangan blokning formasi.
 *
 * Jonli ko'rinish (o'ng panel) O'RNIGA yuqoridagi «Ko'rish» tugmasi
 * ishlatiladi. Sabab: 31 blokning har biri uchun adminda ikkinchi marta
 * render yozish kerak bo'lardi va u sayt bilan ajralib ketardi — xodim
 * adminda bir narsani, saytda boshqasini ko'rardi. Bitta manba (sayt) +
 * `?preview=` havolasi ishonchliroq.
 */
export function BlockConstructor({ blocks, onChange, role, max = 40 }: BlockConstructorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(blocks[0]?._id ?? null);
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Block | null>(null);
  const [preview, setPreview] = useState(true);

  const selected = useMemo(
    () => blocks.find((block) => block._id === selectedId) ?? null,
    [blocks, selectedId],
  );

  /** Tartib `order` maydonida saqlanadi — sayt shu bo'yicha chizadi. */
  function commit(next: Block[]): void {
    onChange(next.map((block, index) => ({ ...block, order: index }) as Block));
  }

  function addBlock(type: BlockType): void {
    const block = createBlock(type, blocks.length);
    commit([...blocks, block]);
    setSelectedId(block._id);
  }

  function duplicate(block: Block): void {
    // `blockSchema.parse` — nusxa sxemaga mos ekaniga kafolat. Oddiy
    // spread bilan nusxalasak, ichki massivlardagi `_id` lar takrorlanib,
    // surishda ikkita element birga siljirdi.
    const copy = blockSchema.parse({ ...structuredClone(block), _id: newBlockId() });
    const at = blocks.findIndex((item) => item._id === block._id);
    const next = [...blocks];
    next.splice(at + 1, 0, copy);
    commit(next);
    setSelectedId(copy._id);
  }

  function remove(block: Block): void {
    const next = blocks.filter((item) => item._id !== block._id);
    commit(next);
    setPendingDelete(null);
    if (selectedId === block._id) setSelectedId(next[0]?._id ?? null);
  }

  function update(block: Block): void {
    commit(blocks.map((item) => (item._id === block._id ? block : item)));
  }

  return (
    <div
      className={cn(
        'grid min-h-0 flex-1 gap-4',
        preview ? 'xl:grid-cols-[280px_1fr_330px]' : 'lg:grid-cols-[280px_1fr]',
      )}
    >
      {/* ─── Chap panel: bloklar ro'yxati (maket `D4`) ────────────────── */}
      <aside className="flex min-h-0 flex-col border border-n-200 bg-white">
        <div className="flex items-center justify-between border-b border-n-100 px-4 py-3.5">
          <span className="text-sm font-semibold text-n-900">Sahifa bloklari</span>
          <span className="font-mono text-xs tabular text-n-600">
            {blocks.length}/{max}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-2">
          <SortableList
            items={blocks as Block[]}
            getId={(block) => block._id}
            onReorder={commit}
            dense
            emptyTitle="Sahifa hali bo‘sh"
            rowClassName={(block) =>
              cn(
                block._id === selectedId
                  ? 'border-l-accent-600 bg-brand-50'
                  : 'border-l-transparent hover:bg-n-50',
                !block.isVisible && 'opacity-60',
              )
            }
            renderRow={(block) => (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <button
                  type="button"
                  onClick={() => setSelectedId(block._id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span
                    className={cn(
                      'block truncate text-[13.5px] font-semibold',
                      block._id === selectedId ? 'text-brand-600' : 'text-n-900',
                    )}
                  >
                    {BLOCK_META[block.type].label}
                  </span>
                  {/* Maketda ikkinchi qator — blok TURI, mono shriftda */}
                  <span className="block truncate font-mono text-[11px] text-n-600">
                    {block.type}
                  </span>
                </button>

                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={block.isVisible ? 'Yashirish' : 'Ko‘rsatish'}
                  onClick={() => update({ ...block, isVisible: !block.isVisible } as Block)}
                >
                  {block.isVisible ? (
                    <Eye className="size-4 text-brand-600" aria-hidden />
                  ) : (
                    <EyeOff className="size-4 text-n-400" aria-hidden />
                  )}
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Nusxa olish"
                  disabled={blocks.length >= max}
                  onClick={() => duplicate(block)}
                >
                  <Copy className="size-4" aria-hidden />
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="O‘chirish"
                  onClick={() => setPendingDelete(block)}
                >
                  <Trash2 className="size-4 text-error-600" aria-hidden />
                </Button>
              </div>
            )}
          />
        </div>

        {/* Maketda «Blok qo'shish» — PUNKTIR chegarali, panel ostida */}
        <div className="border-t border-n-100 p-3.5">
          <button
            type="button"
            disabled={blocks.length >= max}
            onClick={() => setAdding(true)}
            className="flex h-11 w-full items-center justify-center gap-2.5 border border-dashed border-brand-600 bg-brand-50 text-sm font-semibold text-brand-600 hover:bg-brand-600 hover:text-white disabled:cursor-not-allowed disabled:border-n-200 disabled:bg-n-50 disabled:text-n-400"
          >
            <Plus className="size-4" aria-hidden />
            Blok qo‘shish
          </button>
          {blocks.length >= max && (
            <p className="mt-2 text-xs text-n-600">
              Sahifada eng ko‘pi {max} ta blok bo‘lishi mumkin.
            </p>
          )}
        </div>
      </aside>

      {/* ─── O'rta panel: tanlangan blok formasi ──────────────────────── */}
      <section className="min-h-0 overflow-y-auto border border-n-200 bg-white">
        {selected ? (
          <div className="space-y-6 p-4">
            <header className="flex items-center justify-between gap-3 border-b border-n-100 pb-3.5">
              <div>
                <h2 className="text-[17px] font-semibold text-n-900">
                  {BLOCK_META[selected.type].label}
                </h2>
                <p className="font-mono text-xs text-n-600">{selected.type}</p>
              </div>
              <div className="flex items-center gap-2.5">
                {!selected.isVisible && (
                  <span className="bg-n-100 px-2 py-0.5 text-xs text-n-600">Yashirilgan</span>
                )}
                {/*
                  Ko'rinishni yoqib-o'chirish maketda yuqori panelda:
                  forma keng bo'lganda o'ng panel joyni yeb qo'ymasin.
                */}
                <button
                  type="button"
                  onClick={() => setPreview((value) => !value)}
                  className="hidden border border-n-200 px-3 py-1.5 text-[12.5px] font-semibold text-n-800 hover:border-brand-600 xl:block"
                >
                  {preview ? 'Faqat forma' : 'Forma + ko‘rinish'}
                </button>
              </div>
            </header>

            <BlockDataForm block={selected} onChange={update} />

            <div className="border-t border-n-100 pt-4">
              <BlockAppearance
                value={selected.settings}
                onChange={(settings) => update({ ...selected, settings } as Block)}
              />
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-64 flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="text-n-600">
              {blocks.length === 0
                ? 'Sahifa hali bo‘sh — birinchi blokni qo‘shing.'
                : 'Tahrirlash uchun chapdan blok tanlang.'}
            </p>
            {blocks.length === 0 && (
              <Button icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
                Blok qo‘shish
              </Button>
            )}
          </div>
        )}
      </section>

      {/* ─── O'ng panel: sxematik ko'rinish (maket `D4`) ──────────────── */}
      {preview && (
        <aside className="hidden min-h-0 flex-col border border-n-200 bg-n-100 xl:flex">
          <div className="flex items-center justify-between border-b border-n-200 bg-white px-4 py-3">
            <span className="text-[13.5px] font-semibold text-n-900">Jonli ko‘rinish</span>
            <span className="font-mono text-[11.5px] text-n-600">sxema</span>
          </div>
          {/*
            TO'LIQ render EMAS — sxema. Maketning o'zida shu tanlov
            izohlangan: to'liq render sahifani og'irlashtiradi va
            saytdan ajralib ketadi. Bu yerda faqat TARTIB va nisbiy
            o'lcham ko'rsatiladi, to'liq natija «Ko'rish» tugmasi
            orqali saytning O'ZIDA ochiladi.
          */}
          <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-4">
            {blocks.map((block) => (
              <button
                key={block._id}
                type="button"
                onClick={() => setSelectedId(block._id)}
                className={cn(
                  'relative flex flex-col gap-2 border-2 p-3.5 text-left',
                  block._id === selectedId
                    ? 'border-accent-600 bg-white'
                    : 'border-n-200 bg-n-50 hover:border-brand-600',
                  !block.isVisible && 'opacity-45',
                )}
              >
                {block._id === selectedId && (
                  <span className="absolute -top-px -right-px bg-accent-600 px-2 py-0.5 text-[10.5px] font-bold tracking-[0.05em] text-white">
                    TAHRIRLANMOQDA
                  </span>
                )}
                <span className="font-mono text-[10.5px] tracking-[0.1em] text-n-600 uppercase">
                  {block.type}
                </span>
                <span className="text-sm leading-snug font-semibold text-n-900">
                  {blockSummary(block)}
                </span>
                <span className="flex gap-1.5">
                  {schematicBars(block.type).map((height, index) => (
                    <span
                      key={index}
                      style={{ height }}
                      className={cn(
                        'flex-1',
                        block._id === selectedId ? 'bg-brand-100' : 'bg-n-200',
                      )}
                    />
                  ))}
                </span>
              </button>
            ))}
          </div>
        </aside>
      )}

      <AddBlockModal open={adding} role={role} onClose={() => setAdding(false)} onAdd={addBlock} />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Blokni o‘chirish"
        message={
          pendingDelete
            ? `«${BLOCK_META[pendingDelete.type].label}» bloki va uning ichidagi barcha matn o‘chiriladi.`
            : ''
        }
        confirmLabel="O‘chirish"
        danger
        onConfirm={() => pendingDelete && remove(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

/**
 * Ro'yxatdagi ikkinchi qator — blok ichida nima borligini ko'rsatadi.
 *
 * Bittasi bo'lmasa ro'yxatda uchta «Matn» yonma-yon turadi va xodim
 * qaysi biri kerakligini faqat bosib ko'rib bilardi.
 */
function blockSummary(block: Block): string {
  const data = block.data as Record<string, unknown>;

  const title = data.title;
  if (isTranslatable(title) && title.uz) return title.uz;

  if (block.type === 'hero-slider') return `${block.data.slides.length} ta slayd`;
  if (block.type === 'gallery') return `${block.data.images.length} ta rasm`;
  if (block.type === 'rich-text' || block.type === 'text-image') {
    const text = stripHtml(block.data.content.uz);
    return text ? text.slice(0, 60) : 'Matn kiritilmagan';
  }
  if (block.type === 'html') return block.data.code ? 'Kod kiritilgan' : 'Kod kiritilmagan';
  if (block.type === 'map') return block.data.lat === null ? 'Koordinata yo‘q' : 'Xarita';

  return BLOCK_META[block.type].hint;
}

/**
 * Sxematik ustunlar — blok turining «shakli» (maket `D4`).
 *
 * Aniq render emas, faqat nisbiy o'lcham: bir ustunli matn, ikki
 * ustunli matn+rasm, to'rt kartochkali to'r va hokazo. Turi
 * ro'yxatda yo'q bo'lsa bitta to'liq ustun ko'rsatiladi.
 */
function schematicBars(type: string): string[] {
  const SHAPES: Record<string, string[]> = {
    'hero-slider': ['30px'],
    'page-header': ['18px'],
    'rich-text': ['24px'],
    'text-image': ['21px', '30px'],
    stats: ['15px', '15px', '15px', '15px'],
    features: ['21px', '21px', '21px'],
    gallery: ['18px', '18px', '18px'],
    'products-grid': ['27px', '27px', '27px', '27px'],
    'product-slider': ['27px', '27px', '27px'],
    'news-list': ['24px', '24px', '24px'],
    cta: ['21px'],
  };
  return SHAPES[type] ?? ['24px'];
}

function isTranslatable(value: unknown): value is { uz: string } {
  return typeof value === 'object' && value !== null && 'uz' in value;
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
